import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { Bell, ChevronDown, MoreHorizontal, Trash2 } from 'lucide-react'
import {
  Menu,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  Tabs,
  CountryFlagIcon,
} from '../components/ui'
import type { TabDef } from '../components/ui'
import { useWatchlist } from '../contexts/WatchlistContext'
import { useToast } from '../contexts/ToastContext'
import type { WatchlistList, WatchlistItem } from '../services/watchlist'
import { flattenWatchlist } from '../services/watchlist'
import WatchlistRemoveModal from '../components/watchlist/WatchlistRemoveModal'
import WatchlistItemNotificationModal from '../components/notifications/WatchlistItemNotificationModal'
import {
  applyWatchlistItemFilters,
  parseWatchlistSortKey,
  parseWatchlistTypeFilter,
  serialiseWatchlistSortKey,
  serialiseWatchlistTypeFilter,
  watchlistFiltersActive,
  type WatchlistSortKey,
  type WatchlistTypeFilter,
} from '../utils/watchlistFilters'
import { parseWatchlistListFilter } from '../utils/watchlistListFilter'
import { usePageTitle } from '../hooks/usePageTitle'

/**
 * Watchlist page — list rail, type/search/sort toolbar, rows with list pills.
 *
 * URL params (besides `list`):
 *   ?type=   — companies | persons (omitted = all)
 *   ?q=      — text filter (display name + entity id)
 *   ?sort=   — recent (omitted = name A–Z)
 */

const WatchlistPage: React.FC = () => {
  const {
    data,
    lists,
    loading,
    setItemLists,
    removeFromLists,
    removeItem,
    updateItemNotificationPreferences,
  } = useWatchlist()
  const { showToast } = useToast()
  usePageTitle('Watchlist')
  const [searchParams, setSearchParams] = useSearchParams()
  const filter = parseWatchlistListFilter(searchParams.get('list'))
  const typeFilter = parseWatchlistTypeFilter(searchParams.get('type'))
  const sortKey = parseWatchlistSortKey(searchParams.get('sort'))
  const query = searchParams.get('q') || ''
  const [draftQuery, setDraftQuery] = useState(query)
  const [removeItemState, setRemoveItemState] = useState<WatchlistItem | null>(
    null
  )
  const [notifyItemState, setNotifyItemState] = useState<WatchlistItem | null>(
    null
  )

  useEffect(() => {
    setDraftQuery(query)
  }, [query])

  const patchSearchParams = (
    patch: (params: URLSearchParams) => void
  ) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        patch(params)
        return params
      },
      { replace: true }
    )
  }

  const setTypeFilter = (id: WatchlistTypeFilter) => {
    patchSearchParams((params) => {
      const v = serialiseWatchlistTypeFilter(id)
      if (v === null) params.delete('type')
      else params.set('type', v)
    })
  }

  const setSortKey = (key: WatchlistSortKey) => {
    patchSearchParams((params) => {
      const v = serialiseWatchlistSortKey(key)
      if (v === null) params.delete('sort')
      else params.set('sort', v)
    })
  }

  const setQuery = (q: string) => {
    patchSearchParams((params) => {
      const trimmed = q.trim()
      if (!trimmed) params.delete('q')
      else params.set('q', trimmed)
    })
  }

  const uniqueItems = useMemo(() => flattenWatchlist(data), [data])
  const totalCount = uniqueItems.length

  const activeList = useMemo(() => {
    if (!filter.startsWith('l-')) return null
    const id = Number(filter.slice(2))
    return lists.find((l) => l.id === id) ?? null
  }, [filter, lists])

  const listItems: WatchlistItem[] = useMemo(() => {
    if (filter === 'all') return uniqueItems
    if (filter === 'uncategorized') return data.uncategorized
    return activeList ? activeList.items : []
  }, [filter, data, activeList, uniqueItems])

  const typeTabCounts = useMemo(
    () => ({
      all: listItems.length,
      companies: listItems.filter((i) => i.entityType === 'company').length,
      persons: listItems.filter((i) => i.entityType === 'person').length,
    }),
    [listItems]
  )

  const typeTabs: TabDef[] = useMemo(
    () => [
      { id: 'all', label: 'Alle', count: typeTabCounts.all },
      { id: 'companies', label: 'Virksomheder', count: typeTabCounts.companies },
      { id: 'persons', label: 'Personer', count: typeTabCounts.persons },
    ],
    [typeTabCounts]
  )

  const visibleItems = useMemo(
    () =>
      applyWatchlistItemFilters(listItems, {
        type: typeFilter,
        query,
        sort: sortKey,
      }),
    [listItems, typeFilter, query, sortKey]
  )

  const filtersActive = watchlistFiltersActive(typeFilter, query)

  const headingLabel =
    filter === 'all'
      ? 'Alle på watchlist'
      : filter === 'uncategorized'
        ? 'Uden liste'
        : (activeList?.name ?? 'Liste')

  async function handleToggleList(item: WatchlistItem, listId: number) {
    const has = item.listIds.includes(listId)
    const next = has
      ? item.listIds.filter((id) => id !== listId)
      : [...item.listIds, listId]
    try {
      await setItemLists(item.id, next)
      showToast(has ? 'Fjernet fra liste.' : 'Tilføjet til liste.')
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    }
  }

  async function handleSetUncategorized(item: WatchlistItem) {
    if (item.listIds.length === 0) return
    try {
      await setItemLists(item.id, [])
      showToast('Flyttet til uden liste.')
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    }
  }

  return (
    <>
      <section aria-labelledby="watchlist-heading">
          <div className="flex items-baseline justify-between gap-3 mb-1">
            <h1
              id="watchlist-heading"
              className="font-serif text-2xl text-ink"
            >
              {headingLabel}
            </h1>
            <span className="font-mono text-xs text-ink-500 shrink-0">
              {visibleItems.length}{' '}
              {visibleItems.length === 1 ? 'entitet' : 'entiteter'}
            </span>
          </div>

          {!loading && listItems.length > 0 && (
            <div className="mt-2 mb-4 flex flex-wrap items-end gap-x-3 gap-y-2 border-b border-rule -mx-1">
              <Tabs
                dense
                className="mt-0 border-b-0"
                ariaLabel="Filtrér efter entitetstype"
                tabs={typeTabs}
                active={typeFilter}
                onChange={(id) => setTypeFilter(id as WatchlistTypeFilter)}
              />
              <WatchlistToolbar
                className="ml-auto pb-1.5"
                draftQuery={draftQuery}
                onDraftQueryChange={setDraftQuery}
                onQueryCommit={setQuery}
                sortKey={sortKey}
                onSortChange={setSortKey}
              />
            </div>
          )}

          {loading ? (
            <p className="font-mono text-xs uppercase tracking-widest text-ink-500">
              Henter…
            </p>
          ) : visibleItems.length === 0 ? (
            <EmptyState
              filter={filter}
              hasAnyItems={totalCount > 0}
              hasLists={lists.length > 0}
              listHasItems={listItems.length > 0}
              filtersActive={filtersActive}
            />
          ) : (
            <ul className="divide-y divide-rule">
              {visibleItems.map((item) => (
                <WatchlistRow
                  key={item.id}
                  item={item}
                  lists={lists}
                  onToggleList={handleToggleList}
                  onSetUncategorized={handleSetUncategorized}
                  onRequestNotify={() => setNotifyItemState(item)}
                  onRequestRemove={() => setRemoveItemState(item)}
                />
              ))}
            </ul>
          )}
        </section>

      {notifyItemState?.notificationPreferences && (
        <WatchlistItemNotificationModal
          open
          onClose={() => setNotifyItemState(null)}
          displayName={
            notifyItemState.displayName || notifyItemState.entityId
          }
          entityType={notifyItemState.entityType}
          preferences={notifyItemState.notificationPreferences}
          onSave={async (updates) => {
            try {
              const updated = await updateItemNotificationPreferences(
                notifyItemState.id,
                updates
              )
              setNotifyItemState(updated)
              showToast('Notifikationer opdateret.')
              return updated.notificationPreferences ?? { types: [] }
            } catch (err: unknown) {
              showToast(err instanceof Error ? err.message : 'Fejl', {
                tone: 'signal',
              })
              throw err
            }
          }}
        />
      )}

      {removeItemState && (
        <WatchlistRemoveModal
          open
          onClose={() => setRemoveItemState(null)}
          item={removeItemState}
          lists={lists}
          displayLabel={removeItemState.displayName || removeItemState.entityId}
          onRemoveFromLists={async (ids) => {
            await removeFromLists(removeItemState.id, ids)
            showToast('Opdateret.')
            setRemoveItemState(null)
          }}
          onRemoveAll={async () => {
            await removeItem(removeItemState.id)
            showToast('Fjernet fra watchlist.')
            setRemoveItemState(null)
          }}
        />
      )}
    </>
  )
}


function WatchlistRow({
  item,
  lists,
  onToggleList,
  onSetUncategorized,
  onRequestNotify,
  onRequestRemove,
}: {
  item: WatchlistItem
  lists: WatchlistList[]
  onToggleList: (item: WatchlistItem, listId: number) => Promise<void>
  onSetUncategorized: (item: WatchlistItem) => Promise<void>
  onRequestNotify: () => void
  onRequestRemove: () => void
}) {
  const href =
    item.entityType === 'company'
      ? `/company/${item.entityId}`
      : `/person/${item.entityId}`
  const kind = item.entityType === 'company' ? 'Virksomhed' : 'Person'
  const meta =
    item.entityType === 'company' ? `CVR ${item.entityId}` : `#${item.entityId}`

  return (
    <li className="flex items-center gap-2.5 py-2.5">
      <div className="min-w-0 flex-1">
        <Link
          to={href}
          className="inline-flex items-baseline gap-1 min-w-0 max-w-full font-serif text-base leading-snug text-ink hover:text-signal transition-colors"
        >
          <span className="truncate">{item.displayName || meta}</span>
          {item.countryCode && (
            <CountryFlagIcon code={item.countryCode} className="text-base" />
          )}
        </Link>
        <p className="text-xs leading-snug text-ink-500 mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <span className="uppercase tracking-wide">{kind}</span>
          <span className="text-rule" aria-hidden>
            ·
          </span>
          <span className="font-mono">{meta}</span>
        </p>
      </div>

      <Menu
        dense
        placement="auto"
        trigger={<MoreHorizontal className="h-3.5 w-3.5" aria-hidden />}
        triggerAriaLabel={`Handlinger for ${item.displayName || meta}`}
        triggerClassName="p-1 -mr-0.5"
      >
        {(close) => (
          <>
            <MenuLabel>Lister</MenuLabel>
            <MenuItem
              selected={item.listIds.length === 0}
              disabled={item.listIds.length === 0}
              onClick={() => {
                close()
                onSetUncategorized(item)
              }}
            >
              Ingen liste
            </MenuItem>
            {lists.map((l) => (
              <MenuItem
                key={l.id}
                selected={item.listIds.includes(l.id)}
                title={l.name}
                onClick={() => {
                  close()
                  onToggleList(item, l.id)
                }}
              >
                {l.name}
              </MenuItem>
            ))}
            <MenuSeparator />
            {item.notificationPreferences && (
              <MenuItem
                icon={<Bell className="h-3 w-3" />}
                onClick={() => {
                  close()
                  onRequestNotify()
                }}
              >
                Notifikationer
              </MenuItem>
            )}
            <MenuItem
              danger
              icon={<Trash2 className="h-3 w-3" />}
              onClick={() => {
                close()
                onRequestRemove()
              }}
            >
              Fjern
            </MenuItem>
          </>
        )}
      </Menu>
    </li>
  )
}

const TOOLBAR_CONTROL =
  'h-7 box-border border border-rule rounded-sm text-xs leading-none'

const WATCHLIST_SORT_OPTIONS: { value: WatchlistSortKey; label: string }[] = [
  { value: 'name', label: 'Navn (A–Z)' },
  { value: 'recent', label: 'Senest tilføjet' },
]

function WatchlistToolbar({
  className = '',
  draftQuery,
  onDraftQueryChange,
  onQueryCommit,
  sortKey,
  onSortChange,
}: {
  className?: string
  draftQuery: string
  onDraftQueryChange: (value: string) => void
  onQueryCommit: (value: string) => void
  sortKey: WatchlistSortKey
  onSortChange: (key: WatchlistSortKey) => void
}) {
  return (
    <div className={`flex flex-wrap items-center gap-1.5 shrink-0 ${className}`}>
      <label className="block h-7 shrink-0">
        <span className="sr-only">Søg i watchlist</span>
        <input
          type="search"
          value={draftQuery}
          onChange={(e) => {
            onDraftQueryChange(e.target.value)
            onQueryCommit(e.target.value)
          }}
          placeholder="Søg…"
          className={`${TOOLBAR_CONTROL} w-[12rem] sm:w-[14.4rem] appearance-none px-2 bg-paper focus:outline-none focus:ring-1 focus:ring-signal placeholder:text-ink-400`}
        />
      </label>
      <WatchlistSortDropdown sortKey={sortKey} onSortChange={onSortChange} />
    </div>
  )
}

function WatchlistSortDropdown({
  sortKey,
  onSortChange,
}: {
  sortKey: WatchlistSortKey
  onSortChange: (key: WatchlistSortKey) => void
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const activeLabel =
    WATCHLIST_SORT_OPTIONS.find((o) => o.value === sortKey)?.label ?? 'Sorter'

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Sorter watchlist"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex ${TOOLBAR_CONTROL} items-center gap-1 px-2 text-ink-600 hover:text-ink hover:border-ink-300 bg-paper transition-colors`}
      >
        <span>{activeLabel}</span>
        <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Sorter watchlist"
          className="absolute right-0 top-full z-30 mt-1 min-w-[11rem] max-h-[min(16rem,calc(100vh-1.5rem))] overflow-y-auto bg-paper border border-rule shadow-lg rounded-sm py-1"
        >
          {WATCHLIST_SORT_OPTIONS.map((option) => {
            const selected = sortKey === option.value
            return (
              <button
                key={option.value}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => {
                  onSortChange(option.value)
                  setOpen(false)
                }}
                className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs text-ink hover:bg-paper-200 transition-colors"
              >
                <span aria-hidden className="w-3 shrink-0 text-[10px] text-ink-500">
                  {selected ? '✓' : ''}
                </span>
                <span className={selected ? 'font-semibold' : ''}>
                  {option.label}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function EmptyState({
  filter,
  hasAnyItems,
  hasLists,
  listHasItems,
  filtersActive,
}: {
  filter: FilterId
  hasAnyItems: boolean
  hasLists: boolean
  listHasItems: boolean
  filtersActive: boolean
}) {
  if (filtersActive && listHasItems) {
    return (
      <p className="text-sm text-ink-500 italic">
        Ingen entiteter matcher filteret.
      </p>
    )
  }
  if (!hasAnyItems && !hasLists) {
    return (
      <p className="text-sm text-ink-500 border border-rule rounded-sm px-4 py-6">
        Listen er tom. Tilføj fra en virksomheds- eller personprofil, og brug
        lister her til at holde styr på projekter.
      </p>
    )
  }
  if (filter === 'uncategorized') {
    return (
      <p className="text-sm text-ink-500 italic">
        Alle entiteter er på mindst én liste.
      </p>
    )
  }
  if (filter.startsWith('l-')) {
    return (
      <p className="text-sm text-ink-500 italic">
        Ingen entiteter på denne liste endnu — brug «…»-menuen på en post eller
        profilsiden for at tilføje.
      </p>
    )
  }
  return (
    <p className="text-sm text-ink-500 italic">Ingen entiteter at vise.</p>
  )
}

export default WatchlistPage
